import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Loader2, ShieldCheck, ArrowRight, GraduationCap } from 'lucide-react';

export default function SubscribeSuccessPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [message, setMessage] = useState('');
  const timerRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setStatus('error');
      setMessage('No session id found.');
      return;
    }
    let a = 0;
    const poll = async () => {
      a += 1;
      setAttempts(a);
      try {
        const res = await api.get(`/subscription/checkout-status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('paid');
          // Force refresh of user state via full reload after 1.5s
          setTimeout(() => { window.location.href = '/app'; }, 1500);
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('expired');
          setMessage('Payment session expired.');
          return;
        }
        if (a >= 8) {
          setStatus('timeout');
          setMessage('Still processing… check back in a minute. Your payment will be applied automatically.');
          return;
        }
        timerRef.current = setTimeout(poll, 2000);
      } catch (e) {
        setStatus('error');
        setMessage(e.response?.data?.detail || 'Could not verify payment');
      }
    };
    poll();
    return () => timerRef.current && clearTimeout(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {status === 'checking' && (
          <div className="industrial-card border-t-4 border-t-amber-400 text-center py-10">
            <Loader2 size={36} className="text-amber-400 animate-spin mx-auto mb-3" />
            <div className="font-display text-2xl text-zinc-100 tracking-wide">CONFIRMING PAYMENT…</div>
            <div className="text-zinc-500 text-sm mt-2 font-mono">Attempt {attempts}/8</div>
          </div>
        )}
        {status === 'paid' && (
          <div className="industrial-card border-t-4 border-t-lime-400 text-center py-10 glow-lime">
            <ShieldCheck size={40} className="text-lime-400 mx-auto mb-3" />
            <div className="font-display text-3xl text-zinc-100 tracking-wide mb-1">YOU'RE IN.</div>
            <div className="text-zinc-400 text-sm mb-2">
              Welcome to the Academy. The theory section is now unlocked on every solution.
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-lime-400">Redirecting…</div>
          </div>
        )}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div className="industrial-card border-t-4 border-t-amber-400 text-center py-10">
            <GraduationCap size={36} className="text-amber-400 mx-auto mb-3" />
            <div className="font-display text-2xl text-zinc-100 tracking-wide">HANG TIGHT</div>
            <div className="text-zinc-400 text-sm mt-2 mb-4">{message}</div>
            <Link to="/app" className="btn-primary text-sm">
              Back to app <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
